import { BasicTable } from "@synergy/table";
import { Col, Flex, Row } from "antd";
import clsx from "clsx";
import i18next from "i18next";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useAuthContext } from "../../../../../contexts/AuthContext";
import query from "../../../../../server/constants.api";
import logger from "../../../../../server/server";
import useFetch from "../../../../../server/useFetch";
import { PageContent } from "../../../../../styles/global/Pages.styled";
import Theme from "../../../../../styles/Theme/Theme";
import { NoData } from "../../../Atomics/NoData";
import Spinner from "../../../Atomics/Spinner";
import { Widget } from "../../../Atomics/Widget";

/**
 * Componente BuildingsEventWidget
 * @param {Object} item
 * @param {String} currentFloor
 * @param {Object} props
 * Renderiza um widget que mostra o número de eventos em um gráfico de várias barras horizontais
 */

const CustomTable = styled.div`
  .antd-basic-table {
    .ant-table {
      border-color: #e6f7ff;
    }

    .ant-table-thead > tr > th {
      background: transparent;
      color: ${Theme.color["green-deloitte"]};
    }

    .ant-table-thead > tr:hover > th {
      background: transparent;
      color: ${Theme.color["green-deloitte"]};
    }
  }
`;

export const BuildingRoomDetail = ({ item, currentFloor, ...props }) => {
  // Obtém a função de tradução do hook useTranslation
  const { t } = useTranslation();
  const { user, tenant } = useAuthContext();

  useEffect(() => {
    logger.info("User " + user?.Username + " entered BuildingRoomDetail successfully! Tenant: " + tenant);
  }, []);

  const { data: availableRooms, isFetching: availableRoomsIsFetching, refetch: refetchAvailableRooms,
  } = useFetch("/room/vacant", "availableRooms",
    {
      buildingID: item.id === currentFloor.value ? item.id : null,
      floorID: !(item.id === currentFloor.value)
        ? currentFloor?.value
        : null,
    },
    currentFloor?.value ?? false, null, query.middlewareURL);

  const { data: checkinVsReservations, refetch: refetchCheckinVsReservations
  } = useFetch("/room/check-in", "checkinVsReservations",
    {
      buildingID: item.id === currentFloor.value ? item.id : null,
      floorID: !(item.id === currentFloor.value)
        ? currentFloor?.value
        : null,
    },
    currentFloor?.value ?? false, null, query.middlewareURL);

  //API que devolve informação de salas para a tabela
  const {
    data: roomTableData,
    isFetching: roomTableDataIsFetching,
    refetch: refetchRoomTableData,
  } = useFetch(
    "/room/occupation",
    "roomTableData",
    {
      id: currentFloor?.value,
    },
    currentFloor?.value ?? false,
    null,
    query.middlewareURL
  );

  useEffect(() => {
    refetchAvailableRooms();
    refetchCheckinVsReservations();
    refetchRoomTableData();
  }, [currentFloor]);

  return (
    <PageContent
      className={clsx("building_room_details", props?.className)}
      data-testid={"building_room_details"}
    >
      <Row style={{ width: "100%", margin: "0px" }} gutter={[16, 16]}>
        <Col span={24} style={{ alignContent: "center" }}>
          <Row>
            <Col span={10}>
              <Row>
                <Col span={24}>
                  <h3>
                    {item?.name} | {t("Room Details").toUpperCase()}{" "}
                    {/* Changes the title to upperCase and translates it */}
                  </h3>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        {currentFloor ? (
          <>
            <Col span={12}>
              <Widget
                data={{
                  background: "white", // Define o fundo do widget como branco
                  title: t("Vacant Rooms"), // Título do widget traduzido
                  icon: null, // Nenhum ícone definido para o widget
                  charts: [
                    {
                      chartType: "stackedColumnsChartApex", // Tipo do gráfico
                      chartId: "stackedColumnsChartApex", // ID do gráfico
                      height: "200px", // Altura do gráfico
                      isFetching: availableRoomsIsFetching,
                      data: availableRooms
                        ? serializeEmptyRooms(availableRooms, t)
                        : null, // Dados para o gráfico
                    },
                  ],
                  height: "100%", // Altura do gráfico
                }}
              />
            </Col>
            <Col span={12}>
              <Widget
                data={{
                  background: "white", // Define o fundo do widget como branco
                  title: t("Check-In and Room Reservation"), // Título do widget traduzido
                  icon: null, // Nenhum ícone definido para o widget
                  charts: [
                    {
                      chartType: "basicLineChartApex", // Tipo do gráfico
                      chartId: "basicLineChartApex", // ID do gráfico
                      height: "200px", // Altura do gráfico
                      data: checkinVsReservations
                        ? serealizeCheckinVsReservations(
                          checkinVsReservations,
                          null,
                          t
                        )
                        : null, // Dados para o gráfico
                    },
                  ],
                  height: "200px", // Altura do gráfico
                }}
              />
            </Col>
            <Col span={24}>
              <Widget
                data={{
                  background: "white", // Define o fundo do widget como branco
                  title: t("Room Occupation"), // Título do widget traduzido
                  height: "300px",
                }}
              >
                {!roomTableDataIsFetching ? (
                  roomTableData ? (
                    <CustomTable>
                      <BasicTable
                        tableId="roomDetailTable"
                        language={i18next.resolvedLanguage}
                        fetchData={roomTableData}
                        hidePagination={true}
                      />
                    </CustomTable>
                  ) : (
                    <div
                      style={{
                        height: "inherit",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <NoData />
                    </div>
                  )
                ) : (
                  <Flex
                    justify="center"
                    align="center"
                    style={{ height: "200px" }}
                  >
                    <Spinner stroke="green" size="42px" />
                  </Flex>
                )}
              </Widget>
            </Col>
          </>
        ) : null}
      </Row>
    </PageContent>
  );
};

const serializeEmptyRooms = (data, t) => {
  const series = [
    {
      name: t("Vacant"),
      data: [],
    },
    {
      name: t("Booked"),
      data: [],
    },
  ];

  Object.entries(data).forEach((entry) => {
    const [key, value] = entry;
    const name = key.split(".");

    series[0].data.push({ x: t(name[0]), y: value.Total, order: name[0] });
    series[1].data.push({ x: t(name[0]), y: value.Booked, order: name[0] });
  });

  // Ordenar os dados pela chave 'order'
  const sortedSeries = series.map((seriesItem) => ({
    ...seriesItem,
    data: seriesItem.data.sort((a, b) => a.order.localeCompare(b.order)),
  }));

  return {
    series: sortedSeries,
    categories: [],
    xAxisType: null,
    labelFormat: null,
    tooltip: null,
    markerSize: null,
    unit: "",
    columnWidth: 40,
    xAxisLabel: "",
    colors: [Theme.color["green-4"], Theme.color["green-deloitte"]],
    legendPosition: "right",
    legendOffsetY: 50,
  };
};

const serealizeCheckinVsReservations = (data, unit, t) => {
  let series;
  if (!(JSON.stringify(data) === '{}')) {
    series = [
      { name: "maxReservations", data: [] },
      { name: "reservations", data: [] },
      { name: "check-in", data: [] },
    ];
  } else {
    series = [];
  }


  Object.entries(data).map(([date, values]) => {
    series[0].data.push({ x: date.split("T")[0], y: values.maxReservations });
    series[1].data.push({ x: date.split("T")[0], y: values.reservations });
    series[2].data.push({ x: date.split("T")[0], y: values["checked-in"] });
  });

  return {
    series: series,
    categories: [],
    xAxisType: null,
    labelFormat: null,
    tooltip: null,
    markerSize: null,
    unit: unit ?? "",
    markerSize: 3,
    columnWidth: 40,
    xAxisLabel: unit ?? "",
    colors: [Theme.color["green-4"], Theme.color["green-deloitte"]],
  };
};
